/**
 * React hooks and components for tracking element clicks
 *
 * @example Component usage (declarative)
 * <ElementClickTracker
 *   actionOutcome="Cancel"
 *   webElement={{
 *     elementType: "button",
 *     name: "cancel",
 *     location: "enter-mfa"
 *   }}>
 *   <button>Cancel</button>
 * </ElementClickTracker>
 *
 * @example Hook usage
 * const trackClick = useTrackElementClick({
 *   actionOutcome: "Cancel",
 *   webElement: {
 *     elementType: "button",
 *     name: "cancel",
 *     location: "enter-mfa"
 *   }
 * });
 * return <button onClick={trackClick}>Cancel</button>;
 */

import React, { useCallback, useEffect, useRef } from 'react';

import { useCohesion } from '../../CohesionProvider';
import { useViewCorrelationId } from '../../ViewCorrelationIdProvider';
import ElementClickedEvent from './index';

const { createEventPayload } = ElementClickedEvent;

/**
 * @typedef {import('zod').infer<
 *   typeof import('./index').default.ReactComponentInputSchema
 * >} ElementClickedEventSchema
 */

/**
 * Extracts HTML identifier (id or data-qa-target) from a click event.
 * Tries in priority order:
 * 1. The actual clicked element (most specific)
 * 2. Closest anchor/button tag (for nested clicks)
 * 3. The element with the event listener (fallback)
 *
 * @example
 * <button id="save-btn">
 *   <icon />
 *   <span data-qa-target="save-msg">Save</span>
 * </button>
 *
 * Clicking "Save" text → returns "save-msg" (from the span)
 * Clicking the icon → returns "save-btn" (fallback to button)
 *
 * @param {React.MouseEvent} event - The click event
 * @returns {string | null} The id or data-qa-target value, or null if not found
 */
function extractHtmlId(event) {
  /**
   *
   * @param {Element | null}  [element]
   * @returns
   */
  const getHtmlId = (element) => {
    if (!element) return null;
    return element.id || element.getAttribute('data-qa-target') || null;
  };

  const target = event.target instanceof Element ? event.target : null;
  const currentTarget =
    event.currentTarget instanceof Element ? event.currentTarget : null;
  const closestInteractive = target?.closest('a, button') || null;

  return (
    getHtmlId(target) ||
    getHtmlId(closestInteractive) ||
    getHtmlId(currentTarget)
  );
}

/**
 * Extracts text content from a click event, prioritizing the full component text.
 * Tries in priority order:
 * 1. The element with the event listener (full component text)
 * 2. Closest anchor/button tag (for nested clicks)
 * 3. The actual clicked element (most specific)
 *
 * @example
 * <button>
 *   <icon />
 *   <span>Save</span> and continue
 * </button>
 *
 * Clicking "Save" text → returns "Save and continue" (full button text)
 * Clicking the icon → returns "Save and continue" (full button text)
 *
 * @param {React.MouseEvent} event - The click event
 * @returns {string | null} The text content or null if not found
 */
function extractElementText(event) {
  /**
   * @param {HTMLElement | null} [element]
   */
  const getText = (element) => {
    if (!element) return null;
    return element.innerText?.trim() || null;
  };

  const target = event.target instanceof HTMLElement ? event.target : null;
  const currentTarget =
    event.currentTarget instanceof HTMLElement ? event.currentTarget : null;

  return (
    getText(currentTarget) ||
    getText(target?.closest('a, button')) ||
    getText(target)
  );
}

/**
 * Hook for tracking element clicks on the page
 * @param {ElementClickedEventSchema} eventData - Event data
 * @returns {(event: React.MouseEvent) => void} Click handler that sends the tracking event
 */
export function useTrackElementClick(eventData) {
  const { trackEvent } = useCohesion();

  /**
   * Value to group related events together
   * Typically, this requires an ElementViewTracker ancestor with `hasViewCorrelationId` prop
   */
  const viewCorrelationId = useViewCorrelationId();

  // Store latest event data in ref to avoid stale closures
  const latestEventDataRef = useRef(eventData);

  useEffect(() => {
    latestEventDataRef.current = {
      ...eventData,
      ...(viewCorrelationId && { viewCorrelationId }),
    };
  });

  return useCallback(
    (event) => {
      const currentEventData = latestEventDataRef.current;

      /** @type {ElementClickedEventSchema['webElement']} */
      const webElement = {
        ...currentEventData.webElement,

        text:
          currentEventData.webElement.text || extractElementText(event) || '',
        htmlId:
          currentEventData.webElement.htmlId || extractHtmlId(event) || '',
      };

      trackEvent(
        createEventPayload({
          ...currentEventData,
          webElement,
        }),
      );
    },
    [trackEvent],
  );
}

/**
 * @typedef {{
 *   as?: React.ElementType, // Component to render as wrapper
 *   children: React.ReactNode,
 *   onClick?: React.MouseEventHandler // Optional additional click handler
 * }} ElementClickTrackerCommonProps
 */

export const ElementClickTracker = React.forwardRef(
  /**
   * Component wrapper for tracking element clicks in Cohesion
   * @param {ElementClickTrackerCommonProps & ElementClickedEventSchema } props
   */
  function ElementClickTrackerComponent(
    { children, as: Element = 'div', onClick, ...trackingProps },
    ref,
  ) {
    const handleClick = useTrackElementClick(trackingProps);

    const combinedClickHandler = useCallback(
      /**
       * @type React.MouseEventHandler
       */
      (event) => {
        handleClick(event);
        if (onClick) {
          onClick(event);
        }
      },
      [handleClick, onClick],
    );

    // If children is a single element, clone it and add our custom click handler
    if (
      React.Children.count(children) === 1 &&
      React.isValidElement(children)
    ) {
      const child =
        /** @type {React.ReactElement<{ children?: React.ReactNode, onClick?: React.MouseEventHandler, 'data-testid'?: string }>} */ (
          children
        );

      return React.cloneElement(child, {
        onClick: (event) => {
          combinedClickHandler(event);
          if (typeof child.props.onClick === 'function') {
            child.props.onClick(event);
          }
        },
        'data-testid': child.props['data-testid']
          ? `${child.props['data-testid']} element-click-tracker`
          : 'element-click-tracker',
        ...{ ref },
      });
    }

    // Otherwise wrap children in the specified element
    return (
      <Element
        onClick={combinedClickHandler}
        data-testid="element-click-tracker"
        ref={ref}
      >
        {children}
      </Element>
    );
  },
);

/**
 * Conditionally adds click tracking to an element when `trackingConfig` is provided.
 * @param {ElementClickTrackerCommonProps & {
 *   trackingConfig: ElementClickedEventSchema | null,
 * }} props
 */
export const OptionalElementClickTracker = ({
  trackingConfig,
  children,
  ...restProps
}) => {
  // Do not track when config is not provided
  if (!trackingConfig) {
    return children;
  }

  return (
    <ElementClickTracker {...restProps} {...trackingConfig}>
      {children}
    </ElementClickTracker>
  );
};
